import {
  CrownTwoTone,
  CoffeeOutlined,
  PlusOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import {
  Card,
  Avatar,
  Flex,
  Space,
  Button,
  Typography,
  Timeline,
  Tag,
  Modal,
  Spin,
  message,
} from 'antd';
import {
  Company,
  License,
  LicenseEntitlement,
  LicenseStatus,
  LicenseType,
} from '../../../../types';
import { useTheme } from 'styled-components';
import { useMemo, useState } from 'react';
import { dateFormats, licenseEntitlements } from '../../../../constants';
import { terminateLicense } from '../../../../utils/httpRequest';
import CreateLicenseModal from '../createLicenseModal';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import UpdateExtraModal from '../updateExtraModal';

export interface LicenseCardProps {
  id: LicenseEntitlement;
  title: string;
  description: string;
  licenses: License[];
  isTrial: boolean;
  onRefresh: () => void;
}

const getColorForLicenseStatus = (status?: LicenseStatus) => {
  switch (status) {
    case LicenseStatus.TERMINATED:
      return 'red';
    case LicenseStatus.INACTIVE:
      return 'grey';
    case LicenseStatus.IN_COMING:
      return '#88b359';
    default:
      return '';
  }
};

const LicenseCard = ({
  id,
  title,
  description,
  licenses,
  isTrial,
  onRefresh,
}: LicenseCardProps) => {
  const { id: companyID = '' } = useParams();
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCreateLicenseModal, setShowCreateLicenseModal] = useState(false);
  const [showExtendLicenseModal, setShowExtendLicenseModal] = useState(false);
  const [showExtraModal, setShowExtraModal] = useState(false);
  const theme = useTheme();
  const [messageApi, contextHolder] = message.useMessage();

  const isInactive = !licenses.find((i) => i.status === LicenseStatus.ACTIVE);

  const entitlement = licenseEntitlements.find(
    (i) => i.id === licenses[0].entitlement
  );

  const activeLicense = licenses.find((i) => i.status === LicenseStatus.ACTIVE);

  const company = useMemo(
    () => ({
      companyID: licenses[0]?.companyID || companyID,
      companyName: licenses[0]?.companyName,
    }),
    [licenses]
  );

  const handleExtend = () => {};
  const handleTerminate = async () => {
    try {
      setLoading(true);
      await terminateLicense(licenses[0].licenseKey);
      onRefresh();
    } catch (err: any) {
      messageApi.error(err || 'License terminate failed!');
    } finally {
      setShowTerminateModal(false);
      setLoading(false);
    }
  };

  return (
    <Card style={{ minWidth: 300 }}>
      {contextHolder}
      <Card.Meta
        avatar={
          <Avatar
            style={{
              backgroundColor: 'transparent',
              color: theme.antd.colorPrimary,
            }}
            icon={isInactive ? <CoffeeOutlined /> : <CrownTwoTone />}
          />
        }
        title={
          <Flex>
            <Flex flex={1} justify="space-between" align="center">
              <Space>
                {isInactive ? (
                  <Typography.Text type="secondary">{title}</Typography.Text>
                ) : (
                  <Typography.Text>{title}</Typography.Text>
                )}
              </Space>
              <Space>
                {!isInactive && (
                  <Button
                    type="primary"
                    ghost
                    onClick={() => setShowExtendLicenseModal(true)}
                  >
                    <PlusOutlined />
                    Extend
                  </Button>
                )}
                {isInactive ? (
                  <Button
                    type="primary"
                    onClick={() => setShowCreateLicenseModal(true)}
                  >
                    <PlusOutlined />
                    Create License
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    danger
                    onClick={() => setShowTerminateModal(true)}
                  >
                    Terminate
                  </Button>
                )}
              </Space>
            </Flex>
          </Flex>
        }
        description={
          <Space direction="vertical" size={24}>
            {id === LicenseEntitlement.CORE ? (
              <Space>
                <Typography.Text type="secondary">
                  Extra Templates:{' '}
                  <strong>{activeLicense?.extraTemplates || '0'}</strong>
                </Typography.Text>
                <Typography.Link onClick={() => setShowExtraModal(true)}>
                  <PlusCircleOutlined /> Update
                </Typography.Link>
              </Space>
            ) : (
              <Typography.Text type="secondary">{description}</Typography.Text>
            )}
            <Timeline
              items={licenses.map((l) => ({
                color:
                  getColorForLicenseStatus(l.status) || theme.antd.colorPrimary,
                children: (
                  <Space direction="vertical" size={8}>
                    <Space>
                      {l.status === LicenseStatus.TERMINATED && (
                        <Tag color="red">Terminated</Tag>
                      )}
                      {l.status === LicenseStatus.INACTIVE && (
                        <Tag color="grey">Inactive</Tag>
                      )}
                      {l.status === LicenseStatus.ACTIVE && (
                        <Tag color={theme.antd.colorPrimary}>Active</Tag>
                      )}
                      {l.status === LicenseStatus.IN_COMING && (
                        <Tag color="green">Up Coming</Tag>
                      )}
                      {l.type === LicenseType.TRIAL && (
                        <Tag icon={<CoffeeOutlined />} color="orange">
                          Trial
                        </Tag>
                      )}
                    </Space>
                    <Typography.Text type="secondary">
                      {l.startDate} to {l.endDate}
                    </Typography.Text>
                  </Space>
                ),
              }))}
            />
          </Space>
        }
      />
      <Modal
        open={showTerminateModal}
        title={`Terminate ${entitlement?.title} license`}
        onCancel={() => setShowTerminateModal(false)}
        onClose={() => setShowTerminateModal(false)}
        onOk={handleTerminate}
        okText="Terminate Now"
        okButtonProps={{
          type: 'primary',
          danger: true,
        }}
        confirmLoading={loading}
      >
        <Spin spinning={loading}>
          <Space direction="vertical">
            <Typography.Text>
              License will be terminated immediately!
              <br />
              <br />
            </Typography.Text>
          </Space>
        </Spin>
      </Modal>
      <CreateLicenseModal
        open={showExtendLicenseModal}
        company={company as unknown as Company}
        entitlement={id}
        licenseType={LicenseType.PRODUCTION}
        isExtend
        action="extend"
        licenseKey={licenses[0].licenseKey}
        limitStartDate={dayjs(licenses[0]?.endDate)
          .add(1, 'day')
          .format(dateFormats.date)}
        onClose={() => {
          setShowExtendLicenseModal(false);
        }}
        onConfirm={() => {
          setShowExtendLicenseModal(false);
          onRefresh();
        }}
      />
      <CreateLicenseModal
        open={showCreateLicenseModal}
        company={company as unknown as Company}
        entitlement={id}
        licenseType={LicenseType.PRODUCTION}
        onClose={() => {
          setShowCreateLicenseModal(false);
        }}
        onConfirm={() => {
          setShowCreateLicenseModal(false);
          onRefresh();
        }}
      />
      <UpdateExtraModal
        open={showExtraModal}
        license={licenses.find((i) => i.status === LicenseStatus.ACTIVE)}
        onClose={() => {
          setShowExtraModal(false);
        }}
        onConfirm={() => {
          setShowExtraModal(false);
          onRefresh();
        }}
      />
    </Card>
  );
};

export default LicenseCard;
